import generateUUID from './generateUUID'

const tempStoreAriaLabelledbyAttribute = 'data-original-aria-labelledby'

function featureDetectFormValidation() {
  return (typeof document.createElement('input').checkValidity === 'function')
  // TODO: Gives false negative in IE10 & IE11...
  // https://stackoverflow.com/questions/8550642/check-if-a-browser-has-built-in-html5-form-validation
}

class FormValidation {
  constructor(formElement, { errorElementClass = 'form__error-feedback' } = {}) {
    this.formElement = formElement
    this.formElement.setAttribute('novalidate', '')
    this.formInputs = this.formElement.querySelectorAll('input, textarea, select')
    this.errorElementClass = errorElementClass
    this.formValidationSupported = featureDetectFormValidation()
  }

  getElementByName(name) {
    return this.formElement.querySelector(`[name="${name}"]:not([type=hidden])`)
  }

  addInputValidateListeners() {
    this.addInputValidateListenersInput()
    this.addInputValidateListenersBlur()
  }

  addInputValidateListenersInput() {
    if (this.formValidationSupported) {
      this.formInputs.forEach((input) => {
        input.addEventListener('input', () => {
          if (!input.validity.valueMissing && input.checkValidity()) {
            this.inputIsValid(input.getAttribute('name'))
          }
        })
      })
    }
  }

  addInputValidateListenersBlur() {
    if (this.formValidationSupported) {
      this.formInputs.forEach((input) => {
        input.addEventListener('blur', () => {
          if (!input.validity.valueMissing && !input.checkValidity()) {
            this.inputIsInvalid(input.getAttribute('name'))
          }
        })
      })
    }
  }

  setFocusOnFirstError() {
    const firstErrorElement = this.formElement.querySelector('[aria-invalid="true"]')
    if (firstErrorElement) {
      firstErrorElement.focus()
    }
  }

  isValid() {
    let valid = true

    if (this.formValidationSupported) {
      this.formInputs.forEach((input) => {
        if (!input.checkValidity()) {
          valid = false
        }
      })
    }

    return valid
  }

  validateForm() {
    if (this.formValidationSupported) {
      this.formInputs.forEach((input) => {
        if (!input.checkValidity()) {
          this.inputIsInvalid(input.getAttribute('name'))
        }
      })
    }
  }

  removeErrorsFromForm() {
    this.formInputs.forEach((input) => {
      this.inputIsValid(input.getAttribute('name'))
    })
  }

  validateInput(name) {
    if (this.formValidationSupported) {
      const element = this.getElementByName(name)
      if (element && !element.checkValidity()) {
        this.inputIsInvalid(name)
      }
    }
  }

  inputIsValid(name) {
    const element = this.getElementByName(name)
    if (element) {
      element.setAttribute('aria-invalid', 'false')
      this.removeErrorFeedback(name)
    }
  }

  inputIsInvalid(name, customValidityMessage) {
    const input = this.getElementByName(name)
    if (input) {
      input.setAttribute('aria-invalid', 'true')

      if (this.formValidationSupported) {
        if (customValidityMessage) {
          input.setCustomValidity(customValidityMessage)
        }
      }

      this.addErrorFeedback(name, customValidityMessage)

      if (this.formValidationSupported) {
        input.setCustomValidity('')
      }
    }
  }

  jsonapiErrors(data) {
    if (data.errors && Array.isArray(data.errors)) {
      data.errors.forEach((error) => {
        if (error.source && error.source.pointer) {
          const source = error.source.pointer.substr(1).split('/')
          let name = 'contact'
          source.forEach((subPath, index) => {
            if (index === 1000) {
              name = subPath
            } else if (subPath.indexOf('upload-') === 0) {
              name += `[${subPath}][]`
            }
            else {
              name += `[${subPath}]`
            }
          })
          this.inputIsInvalid(name, error.detail)
        }
      })
    }
  }

  addErrorFeedback(name, customValidityMessage) {
    const input = this.getElementByName(name)
    if (input) {
      let errorElement = input.parentNode.querySelector(`.${this.errorElementClass}`)

      if (!errorElement) {
        errorElement = document.createElement('div')
        errorElement.classList.add(this.errorElementClass)
        errorElement.setAttribute('aria-live', 'polite')
        errorElement.id = generateUUID()

        if (input.parentNode.classList.contains("form-check")) {
          input.parentNode.parentNode.appendChild(errorElement)
        } else {
          input.parentNode.appendChild(errorElement)
        }

        if (input.hasAttribute('aria-labelledby') && input.getAttribute('aria-labelledby') !== errorElement.id) {
          input.setAttribute(tempStoreAriaLabelledbyAttribute, input.getAttribute('aria-labelledby'))
        }

        input.setAttribute('aria-describedby', errorElement.id)
      }

      if (this.formValidationSupported) {
        errorElement.textContent = input.validationMessage
      } else {
        errorElement.textContent = customValidityMessage || 'Felaktigt innehåll'
      }
    }
  }

  removeErrorFeedback(name) {
    const input = this.getElementByName(name)
    if (input) {
      input.removeAttribute('aria-describedby')

      if (input.hasAttribute(tempStoreAriaLabelledbyAttribute)) {
        input.setAttribute('aria-describedby', input.getAttribute(tempStoreAriaLabelledbyAttribute))
        input.removeAttribute(tempStoreAriaLabelledbyAttribute)
      }

      const errorElement = input.parentNode.parentNode.querySelector(`.${this.errorElementClass}`)
      if (errorElement) {
        errorElement.parentNode.removeChild(errorElement)
      }
    }
  }
}

export default FormValidation
